import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

interface MiniProductProps {
  image: string;
  name: string;
  price: number;
  badge?: string;
  id: string;
}

const MiniProduct = ({ image, name, price, badge, id }: MiniProductProps) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    addToCart({ id, name, price, type: 'product', image });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-white/3 border border-violet-500/15 rounded-2xl hover:border-violet-500/25 transition-all group">
      <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
        <img src={image} alt={name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
      </div>
      <div className="flex-1 min-w-0">
        {badge && (
          <span className="inline-block px-2 py-0.5 bg-violet-600/25 text-violet-300 text-xs rounded-full mb-1 whitespace-nowrap">{badge}</span>
        )}
        <p className="text-white font-semibold text-sm leading-snug mb-1 line-clamp-2">{name}</p>
        <p className="text-rose-400 font-bold text-sm">&euro;{price.toFixed(2)}</p>
      </div>
      <button
        onClick={handleAddToCart}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shrink-0 ${
          added
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
            : 'bg-rose-600/20 text-rose-300 border border-rose-500/25 hover:bg-rose-600/35'
        }`}
      >
        <div className="w-3.5 h-3.5 flex items-center justify-center">
          <i className={`text-xs ${added ? 'ri-check-line' : 'ri-shopping-cart-line'}`} />
        </div>
        {added ? 'Toegevoegd!' : 'In wagen'}
      </button>
    </div>
  );
};

const BlogCore = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* ── NACHTELIJKE ONRUST BABY — kernblok ───────────────────── */}
      <section className="py-20 bg-gradient-to-b from-[#0a0018] to-[#050010]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-rose-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Kernonderdeel ✦</p>
              <h2 className="text-3xl font-bold text-white mb-4">
                Waarom schrikt jouw baby<br />
                <span className="text-rose-300">elke nacht wakker?</span>
              </h2>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                Een baby die iedere nacht meerdere malen schrikt, onbedaarlijk huilt of moeilijk te kalmeren is —
                terwijl er geen medische oorzaak is — ervaart vaak energetische overbelasting.
                De slaapkamer van een kind is een beschermde ruimte die dat ook moet zijn.
              </p>

              <div className="space-y-4 mb-6">
                {[
                  { title: 'Overprikkeling door omgevingsenergie', desc: 'Bezoekers, familiebezoek, drukke sociale interacties laten energetische sporen achter die baby\'s direct registreren.' },
                  { title: 'Gevoeligheid voor emoties van ouders', desc: 'Stress, zorgen of spanning van ouders trekt direct door naar het kind — baby\'s zijn extreem resonant.' },
                  { title: 'Negatieve energie in de slaapruimte', desc: 'De kamer zelf kan energetische belasting dragen, zeker in woningen met een bewogen verleden.' },
                ].map(item => (
                  <div key={item.title} className="flex gap-3">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-rose-400 text-sm" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">{item.title}</p>
                      <p className="text-white/45 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Product embed */}
              <div className="mb-4">
                <p className="text-violet-400/70 text-xs uppercase tracking-wide font-semibold mb-3">
                  <i className="ri-star-line mr-1" />Speciaal voor baby's
                </p>
                <MiniProduct
                  id="ee-2"
                  image="https://public.readdy.ai/ai/img_res/4bdf6173045f6e6b4ffc32b4b038cf0f.jpg"
                  name="Evil Energy Destroy Baby Hing Pasta"
                  price={12.95}
                  badge="Baby bescherming"
                />
              </div>
              <p className="text-white/35 text-xs leading-relaxed">
                Zachte hing-pasta speciaal voor baby's — in de haren of onder het kussen voor dag- en nachtbescherming.
                Eeuwenoud gebruik in Surinaamse en Hindoestaanse beschermingstraditie.
              </p>
            </div>

            <div className="relative h-[480px] rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=newborn%20baby%20sleeping%20crib%20dark%20warm%20light%20spiritual%20protection%20nursery%20peaceful%20infant%20sleeping%20sacred%20protection%20energy%20gentle%20warm%20candlelight%20soft%20glow%20night%20nursery%20baby%20protected%20calm%20serene&width=700&height=600&seq=babycore1&orientation=portrait"
                alt="Baby beschermd slapend"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050010]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 p-4 bg-[#050010]/70 backdrop-blur-sm border border-violet-500/20 rounded-2xl">
                <p className="text-white/80 text-sm leading-relaxed">
                  <strong className="text-violet-300">"Na het beschermingsritueel sliep mijn baby de volgende nacht al door."</strong>
                  <span className="text-white/50 text-xs block mt-1">— Fatima O., klant Zpiritualz</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INVLOEDEN VAN OMGEVING ────────────────────────────────── */}
      <section className="py-20 bg-[#050010]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Omgevingsinvloeden ✦</p>
            <h2 className="text-3xl font-bold text-white mb-4">
              Familie, vrienden, kennissen —<br />
              <span className="text-violet-400">iedereen laat energie achter</span>
            </h2>
            <p className="text-white/50 text-sm max-w-2xl mx-auto leading-relaxed">
              Energetische invloed van mensen om je heen is zelden bewust negatief — maar de effecten zijn real.
              Jaloezie, zorgen, spanning: dit heeft een meetbare impact op jou, je kind en je thuisomgeving.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              {
                icon: 'ri-parent-line',
                title: 'Familie & bezoek',
                items: ['Familiebezoek brengt soms meer mee dan liefde', 'Bezorgdheid van grootouders kan als druk voelen', 'Na druk familiebezoek voelen baby\'s dit direct'],
              },
              {
                icon: 'ri-user-heart-line',
                title: 'Vrienden & kennissen',
                items: ['Bewondering en jaloezie gaan hand in hand', 'Mensen die het goed met je menen kunnen toch negatieve energie meebrengen', 'Sociale interacties kosten altijd een stukje energie'],
              },
              {
                icon: 'ri-building-line',
                title: 'Werk & collega\'s',
                items: ['Werkconflicten neem je energetisch mee naar huis', 'Nijd van collega\'s heeft effect op je prestaties', 'Een vergiftigde werkomgeving beïnvloedt thuis'],
              },
            ].map(col => (
              <div key={col.title} className="p-6 bg-white/3 border border-violet-500/15 rounded-2xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-violet-600/20 mb-4">
                  <i className={`${col.icon} text-violet-400 text-xl`} />
                </div>
                <h3 className="text-white font-bold text-base mb-4">{col.title}</h3>
                <ul className="space-y-2.5">
                  {col.items.map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-violet-400 text-sm mt-0.5 shrink-0" />
                      <span className="text-white/50 text-xs leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Jaloezie & energie blok */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
            <div className="p-7 bg-gradient-to-br from-rose-900/15 to-violet-900/15 border border-rose-500/15 rounded-3xl">
              <h3 className="text-white font-bold text-xl mb-3">
                <i className="ri-eye-line text-rose-400 mr-2" />
                Jaloezie en haar energetisch effect
              </h3>
              <p className="text-white/55 text-sm leading-relaxed mb-4">
                Jaloezie is een van de sterkste negatieve energetische krachten die iemand kan uitstralen.
                Het hoeft niet bewust te zijn — een gevoel van afgunst bij iemand die jou oprecht goed gezind is,
                heeft dezelfde energetische impact.
              </p>
              <div className="space-y-2">
                {[
                  'Effect op volwassenen: vermoeidheid, emotionele instabiliteit, negatieve gedachten',
                  'Effect op kinderen: overprikkeling, huilbuien, verstoorde slaap',
                  'Kinderen registreren dit sneller en dieper dan volwassenen',
                ].map(item => (
                  <div key={item} className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-rose-400 text-xs mt-0.5 shrink-0" />
                    <span className="text-white/50 text-xs leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-7 bg-gradient-to-br from-violet-900/15 to-fuchsia-900/15 border border-violet-500/15 rounded-3xl">
              <h3 className="text-white font-bold text-xl mb-3">
                <i className="ri-briefcase-4-line text-violet-400 mr-2" />
                Zakelijke impact
              </h3>
              <p className="text-white/55 text-sm leading-relaxed mb-4">
                Ondernemers zijn extra kwetsbaar. Een zaak trekt constant mensen — klanten, leveranciers,
                concurrenten. Negatieve energieën stapelen zich op in de ruimte en beïnvloeden de flow van je bedrijf direct.
              </p>
              <div className="space-y-2">
                {[
                  'Verminderde focus en creativiteit zonder duidelijke reden',
                  'Conflicten met personeel of klanten die blijven herhalen',
                  'Klanten die wegblijven of deals die steeds mislopen',
                  'Stagnerende omzet ondanks goede inspanning',
                ].map(item => (
                  <div key={item} className="flex items-start gap-2">
                    <i className="ri-bar-chart-line text-violet-400 text-xs mt-0.5 shrink-0" />
                    <span className="text-white/50 text-xs leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/consultations')}
                className="mt-4 flex items-center gap-2 px-4 py-2 border border-violet-500/30 text-violet-300 rounded-xl text-sm hover:bg-violet-500/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line text-xs" />
                Plan een zakelijk consult
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogCore;
